import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Box,
  Grid,
  Paper,
  Typography,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Avatar,
  Card,
  CardContent,
  CardHeader,
  Button,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Search as SearchIcon,
  Message as MessageIcon,
  Assignment as TaskIcon,
  Event as ScheduleIcon,
  Favorite as FavoriteIcon,
  Person as PersonIcon,
  MedicalServices as OrderIcon,
  Medication as PrescriptionIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '../../context/LocaleContext';
import ClockInOutManager from '../auth/ClockInOutManager';
import { getRecentSearches } from '../../services/recentSearchService';
import { personalizedDashboardService } from '../../services/personalizedDashboardService';
import '../../styles/dashboard.css';
import { useRecentSearches } from '../../hooks/useRecentSearches';
import { useUserData } from '../../hooks/useUserData';
import { useFavorites } from '../../hooks/useFavorites';

const PANEL_TYPES = {
  RECENT_PATIENTS: 'recentPatients',
  MESSAGES: 'messages',
  TASKS: 'tasks',
  SCHEDULE: 'schedule',
  FAVORITES: 'favorites'
};

const DEFAULT_LAYOUT = [
  { id: PANEL_TYPES.RECENT_PATIENTS, position: 0 },
  { id: PANEL_TYPES.MESSAGES, position: 1 },
  { id: PANEL_TYPES.TASKS, position: 2 },
  { id: PANEL_TYPES.SCHEDULE, position: 3 },
  { id: PANEL_TYPES.FAVORITES, position: 4 }
];

const PersonalizedDashboard = () => {
  const { t, formatDate, formatDateTime } = useLocale();
  const navigate = useNavigate();
  
  // Use the new useUserData hook for consistent data structure
  const { user: currentUser, defaultFacility } = useUserData();
  
  // Only use recent searches when login is complete and we have a facility
  const { navigateToPatient } = useRecentSearches(currentUser?.loginComplete ? defaultFacility?.id : null);

  // Use the favorites hook
  const { favorites, loading: favoritesLoading, loadFavorites } = useFavorites(currentUser?.uid);

  // Only use favorites when we have a valid user ID and the hook is properly initialized
  const safeLoadFavorites = useCallback(() => {
    if (currentUser?.uid && loadFavorites && typeof loadFavorites === 'function') {
      loadFavorites();
    }
  }, [currentUser?.uid, loadFavorites]);

  const [panels, setPanels] = useState(DEFAULT_LAYOUT);
  const [panelData, setPanelData] = useState({
    recentPatients: [],
    messages: [],
    tasks: [],
    schedule: [],
    favorites: []
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Load user's panel layout from service
  const loadUserLayout = useCallback(async () => {
    if (!currentUser?.uid) return;
    
    try {
      const layout = await personalizedDashboardService.loadUserLayout(currentUser.uid);
      setPanels(layout.length > 0 ? layout : DEFAULT_LAYOUT);
    } catch (error) {
      console.error('Error loading user layout:', error);
      setPanels(DEFAULT_LAYOUT);
    }
  }, [currentUser?.uid]);

  // Save user's panel layout to service
  const saveUserLayout = useCallback(async (newLayout) => {
    if (!currentUser?.uid) return;
    
    try {
      await personalizedDashboardService.saveUserLayout(currentUser.uid, newLayout);
    } catch (error) {
      console.error('Error saving user layout:', error);
    }
  }, [currentUser?.uid]);

  // Fetch data for each panel
  const fetchPanelData = useCallback(async () => {
    if (!currentUser?.uid || !defaultFacility?.id) return;
    
    const fetchRecentPatients = async () => {
      try {
        // defaultFacility is now the complete facility object, extract the ID
        const facilityId = defaultFacility.id;
        const recentSearches = await getRecentSearches(currentUser.uid, facilityId);
        return recentSearches.map(search => {
          // Handle name splitting more robustly
          const nameParts = search.name?.trim().split(/\s+/) || [];
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';
          
          return {
            id: search.id,
            firstName,
            lastName,
            name: search.name, // Keep original name for fallback
            mrn: search.mrn,
            searchedAt: search.searchedAt
          };
        });
      } catch (error) {
        console.error('Error fetching recent patients:', error);
        return [];
      }
    };

    const fetchMessages = async () => {
      try {
        if (!defaultFacility?.id) return [];
        return await personalizedDashboardService.getUnreadMessages(currentUser.uid, defaultFacility.id, 5);
      } catch (error) {
        console.error('Error fetching messages:', error);
        return [];
      }
    };

    const fetchTasks = async () => {
      try {
        return await personalizedDashboardService.getPendingTasks(currentUser.uid, 5);
      } catch (error) {
        console.error('Error fetching tasks:', error);
        return [];
      }
    };

    const fetchSchedule = async () => {
      try {
        return await personalizedDashboardService.getUpcomingAppointments(currentUser.uid, 7, 5);
      } catch (error) {
        console.error('Error fetching schedule:', error);
        return [];
      }
    };

    const fetchFavorites = async () => {
      try {
        return await personalizedDashboardService.getUserFavorites(currentUser.uid);
      } catch (error) {
        console.error('Error fetching favorites:', error);
        return [];
      }
    };
    
    setRefreshing(true);
    try {
      const [recentPatients, messages, tasks, schedule, favorites] = await Promise.all([
        fetchRecentPatients(),
        fetchMessages(),
        fetchTasks(),
        fetchSchedule(),
        fetchFavorites()
      ]);

      setPanelData({
        recentPatients,
        messages,
        tasks,
        schedule,
        favorites
      });
    } catch (error) {
      console.error('Error fetching panel data:', error);
    } finally {
      setRefreshing(false);
    }
  }, [currentUser?.uid, defaultFacility?.id]);

  // Handle drag end
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(panels);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update positions
    const newLayout = items.map((item, index) => ({
      ...item,
      position: index
    }));

    setPanels(newLayout);
    saveUserLayout(newLayout);
  };

  useEffect(() => {
    loadUserLayout();
  }, [loadUserLayout]);

  useEffect(() => {
    if (panels.length > 0) {
      Promise.all([
        fetchPanelData()
      ]).then(() => setLoading(false));
    }
  }, [fetchPanelData, panels]);

  // Load favorites when component mounts
  useEffect(() => {
    if (currentUser?.uid) {
      safeLoadFavorites();
    }
  }, [currentUser?.uid, safeLoadFavorites]);

  const renderRecentPatientsPanel = () => (
    <Card className="recent-patients-panel" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={t('dashboard.personalizedDashboard.lastSearchedPatients')}
        action={
          <Tooltip title={t('dashboard.personalizedDashboard.viewAll')}>
            <IconButton onClick={() => navigate('/patients')} sx={{ color: 'white', padding: '4px' }}>
              <ViewIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </Tooltip>
        }
        avatar={<SearchIcon className="panel-header-icon" />}
        sx={{ padding: '8px 12px' }}
      />
      <CardContent sx={{ flex: 1, overflow: 'auto', pt: 0, padding: '8px 12px' }}>
        {panelData.recentPatients.length > 0 ? (
          <List dense sx={{ padding: 0 }}>
            {panelData.recentPatients.map((patient) => (
              <ListItemButton 
                key={patient.id} 
                onClick={async () => {
                  try {
                    await navigateToPatient(patient, navigate);
                  } catch (error) {
                    console.error('Error handling patient click:', error);
                    // Fallback to direct navigation if recent search fails
                    navigate(`/patients/${patient.id}`);
                  }
                }}
                sx={{ px: 1, borderRadius: '6px', mb: 0.5, minHeight: '28px' }}
              >
                <ListItemIcon sx={{ minWidth: '32px', mr: 0.5 }}>
                  <Avatar className="cheerful-avatar" sx={{ width: 24, height: 24 }}>
                    {patient.firstName?.charAt(0) || patient.name?.charAt(0) || 'P'}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={patient.firstName && patient.lastName ? `${patient.firstName} ${patient.lastName}` : patient.name}
                  secondary={`MRN: ${patient.mrn || 'N/A'}`}
                  sx={{ margin: 0 }}
                />
              </ListItemButton>
            ))}
          </List>
        ) : (
          <Typography variant="body2" className="empty-state-text">
            {t('dashboard.personalizedDashboard.noRecentPatients')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const renderMessagesPanel = () => (
    <Card className="messages-panel" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={t('dashboard.personalizedDashboard.myMessages')}
        action={
          <Tooltip title={t('dashboard.personalizedDashboard.viewAll')}>
            <IconButton onClick={() => navigate('/messaging')} sx={{ color: 'white', padding: '4px' }}>
              <ViewIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </Tooltip>
        }
        avatar={<MessageIcon className="panel-header-icon" />}
        sx={{ padding: '8px 12px' }}
      />
      <CardContent sx={{ flex: 1, overflow: 'auto', pt: 0, padding: '8px 12px' }}>
        {panelData.messages.length > 0 ? (
          <List dense sx={{ padding: 0 }}>
            {panelData.messages.map((message) => (
              <ListItem key={message.id} sx={{ px: 1, borderRadius: '6px', mb: 0.5, minHeight: '28px' }}>
                <ListItemText
                  primary={message.subject}
                  secondary={`From: ${message.senderName} - ${formatDateTime(message.createdAt?.toDate())}`}
                  sx={{ margin: 0 }}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" className="empty-state-text">
            {t('dashboard.personalizedDashboard.noUnreadMessages')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const renderTasksPanel = () => (
    <Card className="tasks-panel" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={t('dashboard.personalizedDashboard.myTasks')}
        avatar={<TaskIcon className="panel-header-icon" />}
        sx={{ padding: '8px 12px' }}
      />
      <CardContent sx={{ flex: 1, overflow: 'auto', pt: 0, padding: '8px 12px' }}>
        {panelData.tasks.length > 0 ? (
          <List dense sx={{ padding: 0 }}>
            {panelData.tasks.map((task) => (
              <ListItem key={task.id} sx={{ px: 1, borderRadius: '6px', mb: 0.5, minHeight: '28px' }}>
                <ListItemText
                  primary={task.title}
                  secondary={`Due: ${formatDate(task.dueDate?.toDate())}`}
                  sx={{ margin: 0 }}
                />
                <Chip 
                  label={task.priority} 
                  size="small" 
                  className={`priority-chip-${task.priority}`}
                  sx={{ height: '18px', fontSize: '0.7rem' }}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" className="empty-state-text">
            {t('dashboard.personalizedDashboard.noPendingTasks')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const renderSchedulePanel = () => (
    <Card className="schedule-panel" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={t('dashboard.personalizedDashboard.mySchedule')}
        action={
          <Tooltip title={t('dashboard.personalizedDashboard.viewAll')}>
            <IconButton onClick={() => navigate('/calendar')} sx={{ color: 'white', padding: '4px' }}>
              <ViewIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </Tooltip>
        }
        avatar={<ScheduleIcon className="panel-header-icon" />}
        sx={{ padding: '8px 12px' }}
      />
      <CardContent sx={{ flex: 1, overflow: 'auto', pt: 0, padding: '8px 12px' }}>
        {panelData.schedule.length > 0 ? (
          <List dense sx={{ padding: 0 }}>
            {panelData.schedule.map((appointment) => (
              <ListItem key={appointment.id} sx={{ px: 1, borderRadius: '6px', mb: 0.5, minHeight: '28px' }}>
                <ListItemText
                  primary={`${appointment.patientName}`}
                  secondary={`${formatDateTime(appointment.date?.toDate())} - ${appointment.type}`}
                  sx={{ margin: 0 }}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" className="empty-state-text">
            {t('dashboard.personalizedDashboard.noUpcomingAppointments')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const renderFavoritesPanel = () => (
    <Card className="favorites-panel" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={t('dashboard.personalizedDashboard.myFavorites')}
        avatar={<FavoriteIcon className="panel-header-icon" />}
        sx={{ padding: '8px 12px' }}
      />
      <CardContent sx={{ flex: 1, overflow: 'auto', pt: 0, padding: '8px 12px' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="body2" color="textSecondary">
            {favorites.length} favorites
          </Typography>
          <IconButton 
            size="small" 
            onClick={safeLoadFavorites} 
            disabled={favoritesLoading}
            title="Refresh favorites"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
        
        {favoritesLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
            <CircularProgress size={24} />
          </Box>
        ) : favorites.length > 0 ? (
          <Grid container spacing={1}>
            {favorites.map((favorite) => (
              <Grid item xs={12} key={favorite.id}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={favorite.icon ? <favorite.icon sx={{ fontSize: '1rem' }} /> : <FavoriteIcon sx={{ fontSize: '1rem' }} />}
                  onClick={() => navigate(favorite.path || '/')}
                  sx={{ justifyContent: 'flex-start', minHeight: '32px', fontSize: '0.8rem' }}
                >
                  {favorite.label || favorite.name}
                </Button>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PersonIcon sx={{ fontSize: '1rem' }} />}
                onClick={() => navigate('/patients')}
                sx={{ justifyContent: 'flex-start', minHeight: '32px', fontSize: '0.8rem' }}
              >
                {t('dashboard.personalizedDashboard.accessPatient')}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<OrderIcon sx={{ fontSize: '1rem' }} />}
                onClick={() => navigate('/procedures')}
                sx={{ justifyContent: 'flex-start', minHeight: '32px', fontSize: '0.8rem' }}
              >
                {t('dashboard.personalizedDashboard.placeOrder')}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PrescriptionIcon sx={{ fontSize: '1rem' }} />}
                onClick={() => navigate('/prescriptions')}
                sx={{ justifyContent: 'flex-start', minHeight: '32px', fontSize: '0.8rem' }}
              >
                {t('dashboard.personalizedDashboard.writePrescription')}
              </Button>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );

  const renderPanel = (panelId) => {
    switch (panelId) {
      case PANEL_TYPES.RECENT_PATIENTS:
        return renderRecentPatientsPanel();
      case PANEL_TYPES.MESSAGES:
        return renderMessagesPanel();
      case PANEL_TYPES.TASKS:
        return renderTasksPanel();
      case PANEL_TYPES.SCHEDULE:
        return renderSchedulePanel();
      case PANEL_TYPES.FAVORITES:
        return renderFavoritesPanel();
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box className="dashboard-loading">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box className="cheerful-dashboard">
      <Box className="dashboard-header">
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" component="h1" className="dashboard-title">
            {t('dashboard.title')}
          </Typography>
          <Tooltip title={t('dashboard.personalizedDashboard.refreshDashboard')}>
            <IconButton onClick={fetchPanelData} disabled={refreshing} className="refresh-button">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography variant="body1" className="dashboard-welcome">
          {t('dashboard.personalizedDashboard.welcome', { name: currentUser?.firstName || currentUser?.name || 'User' })}
        </Typography>
      </Box>

      {/* Clock In/Out Manager */}
      <ClockInOutManager />

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="dashboard">
          {(provided) => (
            <Grid
              container
              spacing={1}
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {panels
                .sort((a, b) => a.position - b.position)
                .map((panel, index) => (
                  <Draggable key={panel.id} draggableId={panel.id} index={index}>
                    {(provided, snapshot) => (
                      <Grid
                        item
                        xs={12}
                        md={6}
                        lg={4}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                      >
                        <Paper
                          className={`dashboard-panel ${snapshot.isDragging ? 'dragging' : ''}`}
                          elevation={snapshot.isDragging ? 8 : 2}
                          sx={{
                            height: 280,
                            position: 'relative'
                          }}
                        >
                          <Box
                            {...provided.dragHandleProps}
                            className="dashboard-drag-handle"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              zIndex: 1
                            }}
                          >
                            <DragIcon color="action" />
                          </Box>
                          {renderPanel(panel.id)}
                        </Paper>
                      </Grid>
                    )}
                  </Draggable>
                ))}
              {provided.placeholder}
            </Grid>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
};

export default PersonalizedDashboard; 